// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file socketService.js
 * @description Servicio de Socket.IO con sistema de cola para envío resiliente
 * @version 3.0.0 - CORREGIDO: Cola de mensajes y envío resiliente
 *
 * WebSocket / Socket.IO — causa del error "io is not a function":
 * Antes se usaba `const io = window.io` (script global). Si el bundle no expone
 * `window.io` o expone otro shape, `io` deja de ser invocable.
 * Fix: `import { io } from "socket.io-client"` (API oficial v4; `io` es la factory).
 *
 * Diagnóstico post-fix (si `typeof io === 'function'` pero no hay conexión):
 * - Sin `[SOCKET_CONNECT_ENTER]` → `connect()` no se invoca (SocketContext / Auth).
 * - `[BLOCK_SOCKET_NO_LANGUAGE]` / early return → falta idioma en localStorage.
 * - `[SOCKET_CONNECT_ERROR]` → URL/proxy/CORS/auth/handshake (mensaje y objeto `err`).
 * - `[SOCKET_PREVIOUS_FOUND]` → singleton global previo (HMR o doble init).
 */

import { io } from 'socket.io-client';
import { STORAGE_KEYS } from '../utils/constants.js';
import { normalizeLang } from '../i18n/langNormalize.js';
import { toConversationId } from '../utils/toConversationId.js';
import { devLog } from '../utils/devConsoleLog.js';
import { wsLog } from '../utils/wsLogger.js';
import { logEvent } from '../utils/runtimeLogger.js';
import { debugLog, debugError } from '../utils/debugLogger.js';
import { FEATURES } from '../config/featureFlags.js';
import { invalidateCacheByPattern } from './api.js';
import { publishIncomingChatMessage } from '../utils/incomingMessageBridge.js';

function shouldLogImsRuntime() {
  if (import.meta.env.DEV) return true;
  if (typeof window === 'undefined') return false;
  const host = String(window.location?.hostname ?? '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

/**
 * Mensaje reciente para sonido en rutas de sync/retry.
 * Evita disparar beep por lotes antiguos al reconectar.
 * @param {object|null|undefined} data
 * @param {number} windowMs
 * @returns {boolean}
 */
function isRecentIncomingForSound(data, windowMs = 90000) {
  if (!data || typeof data !== 'object') return false;
  const rawTs = data.created_at ?? data.fecha_envio ?? data.timestamp ?? null;
  if (!rawTs) return true;
  const rawText = String(rawTs);
  let ts = typeof rawTs === 'number' ? rawTs : Date.parse(rawText);
  if (!Number.isFinite(ts) && /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(rawText)) {
    // Payload SQL sin timezone: interpretar como UTC para evitar falsos descartes.
    ts = Date.parse(rawText.replace(' ', 'T') + 'Z');
  }
  if (!Number.isFinite(ts)) return true;
  return Math.abs(Date.now() - Number(ts)) <= windowMs;
}

/**
 * ID de conversación unificado desde payload WS (snake, camel, español).
 * @param {object|null|undefined} message
 * @returns {string|number|null}
 */
export function resolveConversationId(message) {
  if (!message || typeof message !== 'object') return null;
  const v =
    message.conversation_id ??
    message.cid ??
    message.conversationId ??
    message.conversacion_id ??
    null;
  if (v == null || v === '') return null;
  return v;
}

/**
 * Normalización única del payload WS antes del store (texto + ids alineados).
 * @param {object} message
 * @returns {object}
 */
export function normalizeIncomingMessage(message) {
  if (message == null || typeof message !== 'object') {
    return {};
  }
  const display =
    message.display_text != null && String(message.display_text).trim() !== ''
      ? String(message.display_text).trim()
      : '';
  const text =
    display ||
    message.text ||
    message.message ||
    message.body ||
    message.mensaje ||
    message.contenido ||
    message.contenido_original ||
    '';

  const conversationIdRaw =
    message.conversation_id ??
    message.conversacion_id ??
    message.conversationId ??
    message.cid ??
    message.chat_id ??
    message.chatId ??
    null;
  const cidNum = toConversationId(conversationIdRaw);
  let conversationId =
    cidNum != null ? String(cidNum) : conversationIdRaw != null ? String(conversationIdRaw).trim() : '';
  conversationId = String(conversationId || '');

  const sid =
    message.sender_id ??
    message.remitente_id ??
    message.emisor_id ??
    message.usuario_id ??
    message.from_user_id;
  const rawSubj = message.subject_id ?? message.subjectId ?? message.subject_row_id;
  const subjN = rawSubj != null && rawSubj !== '' ? Number(rawSubj) : NaN;
  const subject_id = Number.isFinite(subjN) && subjN > 0 ? subjN : undefined;
  return {
    ...message,
    display_text: display || (message.display_text != null ? String(message.display_text) : ''),
    text,
    message: text,
    mensaje: text || message.mensaje,
    conversation_id: conversationId,
    conversacion_id: conversationId,
    conversationId,
    sender_id: sid,
    remitente_id: message.remitente_id ?? message.sender_id ?? message.emisor_id ?? message.usuario_id ?? sid,
    receiver_id: message.receiver_id ?? message.receptor_id ?? message.recipient_id,
    ...(subject_id !== undefined ? { subject_id } : {}),
  };
}

// Logs forenses: en consola del navegador ejecutar window.DEBUG_SOCKET = true
devLog('🧪 SOCKET BUILD VERSION:', 'WS_FIX_DEPLOY_FINAL');

/**
 * Servicio de sockets con Socket.IO y sistema de cola
 */
class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.socketReady = false; // Estado más específico para envío
    this.userId = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    
    // CORRECCIÓN: Sistema de cola para mensajes pendientes
    this.pendingMessages = [];
    this.pendingEvents = [];
    this.maxPendingMessages = 50; // Límite de mensajes en cola
    this.connectionPromise = null; // Promise para esperar conexión
    this.connectionResolve = null;
    /** @type {(() => string)|null} Inyectado desde MessagesContext; no duplicar estado aquí. */
    this._getActiveConversationId = null;
    /** Idioma usado en el último handshake (evita reutilizar socket si localStorage cambió). */
    this.lastConnectLanguage = null;
    /** Handler directo desde MessagesProvider (store; sin event bus window). */
    this._chatMessageHandler = null;
    /** Suscripciones `on()` antes de `io()` — se aplican al crear el socket (evita warnings y pérdida de listeners). */
    this._pendingListeners = [];
  }

  /**
   * Registra listeners encolados antes de que existiera `this.socket`.
   */
  _flushPendingListeners() {
    if (!this.socket || this._pendingListeners.length === 0) return;
    const batch = [...this._pendingListeners];
    this._pendingListeners = [];
    batch.forEach(({ event, callback }) => {
      try {
        this.socket.on(event, callback);
      } catch (e) {
        devLog('[SOCKET] Error adjuntando listener pendiente:', event, e?.message || e);
      }
    });
    if (typeof console !== 'undefined' && batch.length > 0) {
      console.log('[PRESENCE_FLOW][SOCKET_LISTENER_ATTACHED]', { count: batch.length });
    }
  }

  /**
   * Registra el callback para mensajes entrantes `chat:message`.
   * @param {((msg: object) => void)|null} fn
   */
  setChatHandler(fn) {
    this._chatMessageHandler = typeof fn === 'function' ? fn : null;
    if (typeof console !== 'undefined') {
      console.error('[HANDLER_REGISTERED]', typeof fn);
    }
    if (typeof window !== 'undefined') {
      window.__ARCHAT_HANDLER__ = this._chatMessageHandler;
      if (typeof console !== 'undefined') {
        console.error('[HANDLER_GLOBAL]', window.__ARCHAT_HANDLER__);
      }
    }
  }

  /**
   * `chat:message` → normaliza → evento global → MessagesContext (sin sonido ni lógica extra).
   */
  _bindMessageHandler() {
    if (!this.socket) return;
    this.socket.off('chat:message');
    this.socket.on('chat:message', (msg) => {
      if (shouldLogImsRuntime()) {
        console.log('[IMS_WS_RAW_EVENT]', {
          conversation_id:
            msg?.conversation_id ??
            msg?.conversacion_id ??
            msg?.conversationId ??
            msg?.cid ??
            null,
          message_id: msg?.id ?? msg?.message_id ?? msg?.mensaje_id ?? msg?.temp_id ?? null,
          sender_id:
            msg?.sender_id ?? msg?.remitente_id ?? msg?.emisor_id ?? msg?.usuario_id ?? null,
        });
      }
      console.log('[WS_MESSAGE_RECEIVED]', msg);
      const isTraceMsg = typeof msg?.mensaje === 'string' && msg.mensaje.includes('PRO_V2_REALTIME_ROOTCAUSE_001');
      if (isTraceMsg) {
        console.log('[DEBUG_TRACE][FE_RAW_WS_PAYLOAD]', JSON.stringify({
          id: msg?.id, conversation_id: msg?.conversation_id ?? msg?.conversacion_id,
          remitente_id: msg?.remitente_id ?? msg?.sender_id,
          mensaje: String(msg?.mensaje ?? '').slice(0, 200),
          display_text: String(msg?.display_text ?? '').slice(0, 200),
          contenido_original: String(msg?.contenido_original ?? '').slice(0, 200),
          traducciones: msg?.traducciones,
          translations_map: msg?.translations_map,
          source_language: msg?.source_language,
          target_language: msg?.target_language,
          translation_failed: msg?.translation_failed,
          subject_id: msg?.subject_id ?? msg?.subject_row_id,
        }));
      }
      try {
        if (
          typeof localStorage !== 'undefined' &&
          localStorage.getItem('ARCHAT_DEBUG_LANGUAGE_RUNTIME') === '1'
        ) {
          console.info('[LANG_WS_INCOMING_PAYLOAD]', {
            message_id: msg?.id ?? msg?.message_id ?? msg?.mensaje_id ?? null,
            conversation_id: msg?.conversation_id ?? msg?.conversacion_id ?? null,
            mensaje: String(msg?.mensaje ?? msg?.text ?? msg?.display_text ?? '').slice(0, 120),
            contenido_original: String(msg?.contenido_original ?? '').slice(0, 120),
            source_language: msg?.source_language ?? msg?.idioma_original ?? null,
            target_language: msg?.target_language ?? null,
            translation_failed: msg?.translation_failed ?? null,
            traducciones_keys: msg?.traducciones ? Object.keys(msg.traducciones) : null,
            translations_map_keys: msg?.translations_map ? Object.keys(msg.translations_map) : null,
            ts: Date.now(),
          });
        }
      } catch (_) { /* noop */ }
      const senderId = msg?.sender_id || msg?.remitente_id || msg?.usuario_id || null;
      console.log('[WS_EVENT_DISPATCH]', senderId);
      const normalized = normalizeIncomingMessage(msg);
      if (isTraceMsg) {
        console.log('[DEBUG_TRACE][FE_NORMALIZED_WS]', JSON.stringify({
          id: normalized?.id, conversation_id: normalized?.conversation_id ?? normalized?.conversacion_id,
          text: String(normalized?.text ?? '').slice(0, 200),
          mensaje: String(normalized?.mensaje ?? '').slice(0, 200),
          display_text: String(normalized?.display_text ?? '').slice(0, 200),
          contenido_original: String(normalized?.contenido_original ?? '').slice(0, 200),
          traducciones_keys: normalized?.traducciones ? Object.keys(normalized.traducciones) : null,
          translations_map_keys: normalized?.translations_map ? Object.keys(normalized.translations_map) : null,
          source_language: normalized?.source_language,
          target_language: normalized?.target_language,
          translation_failed: normalized?.translation_failed,
          subject_id: normalized?.subject_id,
        }));
        console.log('[DEBUG_TRACE][FE_SENDER_ID]', JSON.stringify({
          raw_sender: msg?.sender_id ?? msg?.remitente_id,
          normalized_sender: normalized?.sender_id ?? normalized?.remitente_id,
          currentUser: this.userId,
          isOwn: normalized?.sender_id != null && this.userId != null && String(normalized?.sender_id) === String(this.userId),
        }));
      }
      try {
        if (
          typeof localStorage !== 'undefined' &&
          localStorage.getItem('ARCHAT_DEBUG_SUBJECT_MESSAGES') === '1' &&
          typeof console !== 'undefined'
        ) {
          // eslint-disable-next-line no-console
          console.info('[ARCHAT_DEBUG_SUBJECT_MESSAGES][WS chat:message]', {
            event: 'chat:message',
            message_id: normalized?.id ?? normalized?.mensaje_id ?? msg?.id,
            conversation_id: normalized?.conversacion_id ?? normalized?.conversation_id,
            conversacion_id: normalized?.conversacion_id ?? msg?.conversacion_id,
            subject_id: normalized?.subject_id ?? msg?.subject_id ?? msg?.subject_row_id ?? null,
            subjectId: msg?.subjectId ?? null,
            remitente_id: normalized?.remitente_id ?? normalized?.sender_id ?? msg?.remitente_id,
            mensaje: String(normalized?.mensaje ?? normalized?.text ?? msg?.mensaje ?? '').slice(0, 160),
            fecha_envio: normalized?.fecha_envio ?? msg?.fecha_envio ?? msg?.timestamp ?? null,
          });
        }
        if (
          typeof localStorage !== 'undefined' &&
          localStorage.getItem('ARCHAT_DEBUG_MESSAGE_ORDER') === '1' &&
          typeof console !== 'undefined'
        ) {
          // eslint-disable-next-line no-console
          console.info('[ARCHAT_DEBUG_MESSAGE_ORDER][WS chat:message]', {
            event: 'chat:message',
            message_id: normalized?.id ?? normalized?.mensaje_id ?? msg?.id,
            conversation_id: normalized?.conversacion_id,
            subject_id: normalized?.subject_id ?? null,
            fecha_envio: normalized?.fecha_envio ?? msg?.fecha_envio ?? msg?.timestamp ?? null,
          });
        }
      } catch (_) {
        /* noop */
      }
      if (shouldLogImsRuntime()) {
        console.log('[IMS_WS_NORMALIZED]', {
          conversation_id:
            normalized?.conversation_id ??
            normalized?.conversacion_id ??
            normalized?.conversationId ??
            null,
          message_id:
            normalized?.id ??
            normalized?.message_id ??
            normalized?.mensaje_id ??
            normalized?.temp_id ??
            null,
          sender_id:
            normalized?.sender_id ??
            normalized?.remitente_id ??
            normalized?.emisor_id ??
            normalized?.usuario_id ??
            null,
        });
      }
      try {
        if (
          typeof localStorage !== 'undefined' &&
          localStorage.getItem('archatDebugIncomingPayload') === '1' &&
          typeof console !== 'undefined'
        ) {
          const m = msg && typeof msg === 'object' ? msg : {};
          const n = normalized && typeof normalized === 'object' ? normalized : {};
          const safeKeys = Object.keys(m).filter(
            (k) => !/token|password|secret|auth|bearer|cookie/i.test(String(k))
          );
          console.log('[ARCHAT_DEBUG_INCOMING]', {
            message_id: n.id ?? n.mensaje_id ?? m.id,
            conversacion_id: n.conversacion_id ?? n.conversation_id,
            emisor_id: n.sender_id ?? n.remitente_id,
            usuario_actual_socket: this.userId,
            remitente_nick_raw: m.remitente_nick,
            remitente_nick_en_spread: n.remitente_nick,
            raw_keys: safeKeys.slice(0, 60),
            sender_title_candidates: {
              remitente_nick: m.remitente_nick,
              sender_display_name: m.sender_display_name,
              nick: m.nick,
              nombre: m.nombre,
            },
          });
        }
      } catch (_) {
        /* diagnóstico opcional */
      }
      const normalizedSenderId =
        normalized?.sender_id ?? normalized?.remitente_id ?? senderId ?? null;
      const isOwn =
        normalizedSenderId != null &&
        this.userId != null &&
        String(normalizedSenderId) === String(this.userId);
      console.log('[MESSAGE_EVENT_FIXED]', {
        senderId,
        isOwn,
      });
      const newMessageDetail = {
        ...normalized,
        contactId: String(normalizedSenderId ?? senderId ?? ''),
      };
      window.dispatchEvent(
        new CustomEvent('archat:new_message', {
          detail: newMessageDetail,
        })
      );
      publishIncomingChatMessage(newMessageDetail);

      if (
        FEATURES.FEATURE_MESSAGE_STATUS_V1 &&
        !isOwn &&
        this.socket &&
        this.socketReady
      ) {
        const mid = Number(normalized?.id ?? normalized?.mensaje_id ?? msg?.id);
        const cidAck = String(
          normalized?.conversation_id ?? normalized?.conversacion_id ?? ''
        ).trim();
        if (Number.isFinite(mid) && mid > 0 && cidAck) {
          try {
            this.socket.emit('message:delivered', {
              message_id: mid,
              conversation_id: cidAck,
              conversacion_id: cidAck,
            });
          } catch (e) {
            if (typeof console !== 'undefined') {
              console.warn('[message:delivered][emit]', e?.message || e);
            }
          }
        }
      }
    });
  }

  /**
   * Query enviado en handshake/reconexión; requiere idioma persistido.
   * @param {string|number|null} userId
   * @param {string|null} explicitLanguage
   * @returns {{ userId: string|number, token: string|null, tenantId: string|null, language: string }|null}
   */
  buildQueryPayload(userId, explicitLanguage = null) {
    let languageRaw = explicitLanguage;
    try {
      if ((languageRaw == null || String(languageRaw).trim() === '') && typeof localStorage !== 'undefined') {
        languageRaw = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
      }
    } catch {
      languageRaw = null;
    }
    if (languageRaw == null || String(languageRaw).trim() === '') {
      return null;
    }
    return {
      userId,
      token: typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEYS.TOKEN) || null : null,
      tenantId: typeof localStorage !== 'undefined' ? localStorage.getItem('archat_tenant_id') || null : null,
      language: normalizeLang(String(languageRaw).trim()),
    };
  }

  /**
   * Tras cambiar idioma en UI: actualiza auth del cliente, emite update_language y prepara reconexión con idioma nuevo.
   * @param {string} lang - Código ya normalizado (p. ej. en, es)
   */
  syncLanguageWithServer(lang) {
    const normalized = normalizeLang(lang);
    try {
      if (
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('ARCHAT_DEBUG_LANGUAGE_RUNTIME') === '1'
      ) {
        console.info('[LANG_SOCKET_RECONNECT]', {
          newLang: normalized,
          hasSocket: !!this.socket,
          connected: !!this.socket?.connected,
          lastConnectLanguage: this.lastConnectLanguage,
          ts: Date.now(),
        });
      }
    } catch (_) {
      // noop
    }
    if (!this.socket) {
      devLog('[SOCKET_LANG_SYNC] sin socket aún; idioma quedará en auth en próximo connect()', normalized);
      return;
    }
    const queryPayload = this.buildQueryPayload(this.userId, normalized);
    if (!queryPayload) {
      console.error('[SOCKET_ABORT_NO_LANGUAGE]');
      return;
    }
    if (this.socket.io?.opts) {
      this.socket.io.opts.query = { ...queryPayload };
    }
    if (this.socket.connected) {
      this.socket.emit('update_language', { language: normalized });
      devLog('[SOCKET_LANG_SYNC] emit update_language', normalized);
    } else {
      this.emit('update_language', { language: normalized }, { queueIfNotConnected: true });
      devLog('[SOCKET_LANG_SYNC] encolado update_language', normalized);
    }
    this.lastConnectLanguage = normalized;

    this._persistLanguagePreference(normalized);
  }

  /**
   * Persiste idioma_preferido en la DB vía API para que el cambio se refleje en contactos de otros usuarios.
   * @param {string} lang - Código de idioma normalizado
   * @private
   */
  _persistLanguagePreference(lang) {
    const userId = this.userId;
    if (!userId) return;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('archat_token') : null;
    if (!token) return;
    const tenantId = typeof localStorage !== 'undefined' ? localStorage.getItem('archat_tenant_id') || '1' : '1';
    try {
      fetch('/api/perfil.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
        },
        body: JSON.stringify({ usuario_id: userId, idioma_preferido: lang }),
      }).then((res) => {
        if (!res.ok) {
          console.warn('[LANG_PERSIST_FAIL]', res.status);
        } else {
          devLog('[LANG_PERSIST_OK]', { userId, lang });
          try { localStorage.setItem('archat_lang_persisted', lang); } catch (_) { /* noop */ }
        }
      }).catch((err) => {
        console.warn('[LANG_PERSIST_ERR]', err?.message || err);
      });
    } catch (e) {
      console.warn('[LANG_PERSIST_ERR]', e?.message || e);
    }
  }

  /**
   * Inyecta el lector de conversación activa desde MessagesContext (única fuente de verdad).
   * @param {(() => string)|null} fn - Debe devolver el id actual como string (vacío = ninguna).
   */
  setActiveConversationIdGetter(fn) {
    this._getActiveConversationId = typeof fn === 'function' ? fn : null;
  }

  /**
   * ID de conversación activa en UI (solo lectura; estado vive en MessagesContext).
   * @returns {string}
   */
  getActiveConversationId() {
    try {
      const g = this._getActiveConversationId;
      if (!g) return '';
      return String(g() ?? '');
    } catch {
      return '';
    }
  }

  /**
   * Espera hasta que el socket esté listo para envío
   * @param {number} timeout - Timeout en ms (por defecto 10s)
   * @returns {Promise<boolean>} True si está listo, false si timeout
   */
  async waitForReady(timeout = 10000) {
    if (this.socketReady) {
      return true;
    }

    // Si ya hay una promesa de conexión, usarla
    if (this.connectionPromise) {
      try {
        await Promise.race([
          this.connectionPromise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), timeout))
        ]);
        return this.socketReady;
      } catch (error) {
        console.warn('⚠️ [SOCKET] Timeout esperando conexión:', error.message);
        return false;
      }
    }

    return false;
  }

  /**
   * Conecta al servidor de sockets.
   * @param {string|number|{ userId: string|number, language?: string|null }} arg - ID o payload con idioma (fallback si no hay en localStorage).
   */
  connect(arg) {
    let userId;
    /** @type {string|null} */
    let connectLanguageOverride = null;
    if (arg != null && typeof arg === 'object' && !Array.isArray(arg) && 'userId' in arg) {
      userId = arg.userId;
      const rawLang = arg.language;
      connectLanguageOverride =
        rawLang != null && String(rawLang).trim() !== '' ? String(rawLang).trim() : null;
    } else {
      userId = arg;
    }

    if (typeof console !== 'undefined') {
      let tokenProbe = null;
      try {
        tokenProbe =
          typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEYS.TOKEN) : null;
      } catch {
        tokenProbe = null;
      }
      console.log('[SOCKET_CONNECT_ENTER]', {
        userId: this.userId,
        userIdArg: userId,
        languageOverride: connectLanguageOverride,
        token: tokenProbe ? 'OK' : 'MISSING',
      });
    }

    if (!userId) {
      logEvent('WS_CONNECT_SKIPPED_NO_USER', 'socketService', { userId: userId ?? null });
      console.warn('⚠️ [SOCKET] connect() sin userId');
      return this.socket;
    }

    logEvent('WS_CONNECT_CALL', 'socketService', { userId });

    if (this.socket && this.socket.connected) {
      logEvent('WS_ALREADY_CONNECTED', 'socketService');
      return this.socket;
    }

    if (this.isConnecting && this.socket) {
      logEvent('WS_ALREADY_EXISTS', 'socketService', { userId, reason: 'connecting' });
      return this.socket;
    }

    if (typeof window !== 'undefined' && window.__ARCHAT_SOCKET__) {
      const previousSocket = window.__ARCHAT_SOCKET__;
      try {
        console.warn('[SOCKET_KILL_PREVIOUS]');
        previousSocket.disconnect();
      } catch (_) {
        // noop
      }
      window.__ARCHAT_SOCKET__ = null;
      if (this.socket === previousSocket) {
        this.socket = null;
        this.isConnected = false;
        this.isConnecting = false;
        this.socketReady = false;
        this.lastConnectLanguage = null;
      }
    }

    this.userId = userId;
    /** Mismo origen que la SPA (p. ej. http://localhost/app/ → WS vía nginx /socket.io/). */
    const SOCKET_URL = typeof window !== 'undefined' ? window.location.origin : '';
    this._lastSocketUrl = SOCKET_URL;
    try {
      console.log('[WS_PROXY_ORIGIN_URL]', SOCKET_URL);
    } catch (_) {
      // noop
    }
    const tenantIdForGuard =
      typeof localStorage !== 'undefined' ? localStorage.getItem('archat_tenant_id') || null : null;

    let storedLangRaw = null;
    try {
      if (typeof localStorage !== 'undefined') {
        storedLangRaw = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
      }
    } catch {
      storedLangRaw = null;
    }
    if (storedLangRaw == null || String(storedLangRaw).trim() === '') {
      storedLangRaw =
        connectLanguageOverride != null && connectLanguageOverride !== ''
          ? connectLanguageOverride
          : 'es';
      if (typeof console !== 'undefined') {
        console.log('[SOCKET_LANGUAGE_FALLBACK]', {
          userId,
          source: connectLanguageOverride != null ? 'connect({language})' : 'default_es',
          language: storedLangRaw,
        });
      }
    }
    const language = normalizeLang(String(storedLangRaw).trim());
    try {
      console.log('[SOCKET_CONNECT_FINAL]', { language });
    } catch {
      // noop
    }

    if (
      this.socket &&
      (!this.socket.io?.opts?.query?.language)
    ) {
      try {
        console.warn('[SOCKET_RECREATE_NO_LANGUAGE]');
      } catch (_) {
        // noop
      }
      const prevSocket = this.socket;
      try {
        prevSocket.removeAllListeners();
      } catch (_) {
        // noop
      }
      try {
        prevSocket.disconnect();
      } catch (_) {
        // noop
      }
      this.socket = null;
      this.isConnected = false;
      this.socketReady = false;
      this.isConnecting = false;
      this.lastConnectLanguage = null;
      if (typeof window !== 'undefined' && window.__ARCHAT_SOCKET__ === prevSocket) {
        window.__ARCHAT_SOCKET__ = null;
      }
      if (this.connectionResolve) {
        this.connectionResolve(false);
        this.connectionResolve = null;
      }
      this.connectionPromise = null;
    }

    const queryPayload = this.buildQueryPayload(userId, language);
    if (!queryPayload) {
      console.error('[BLOCK_SOCKET_NO_LANGUAGE]', {
        userId,
        tenantId: tenantIdForGuard,
      });
      return this.socket;
    }
    const { tenantId: tenantIdLog, token: tokenLog } = queryPayload;
    try {
      console.log('[SOCKET_QUERY_PAYLOAD]', queryPayload);
    } catch {
      // noop
    }
    try {
      console.warn('[SOCKET_CONNECT_CALLED]', {
        language,
        userId,
        tenantId: tenantIdLog,
        token: tokenLog,
        timestamp: Date.now(),
      });
    } catch (_) {
      // noop
    }

    if (
      this.socket != null &&
      this.lastConnectLanguage != null &&
      this.lastConnectLanguage !== language
    ) {
      const prevSocket = this.socket;
      console.log('[SOCKET_REINIT_FOR_LANGUAGE]', { from: this.lastConnectLanguage, to: language });
      try {
        prevSocket.removeAllListeners();
      } catch (_) {
        // noop
      }
      try {
        prevSocket.disconnect();
      } catch (_) {
        // noop
      }
      this.socket = null;
      this.isConnected = false;
      this.socketReady = false;
      this.isConnecting = false;
      this.lastConnectLanguage = null;
      if (typeof window !== 'undefined' && window.__ARCHAT_SOCKET__ === prevSocket) {
        window.__ARCHAT_SOCKET__ = null;
      }
      if (this.connectionResolve) {
        this.connectionResolve(false);
        this.connectionResolve = null;
      }
      this.connectionPromise = null;
    }

    devLog('🔌 [SOCKET] Conectando a:', SOCKET_URL);

    // Evitar múltiples inicializaciones: reutilizar socket singleton existente (mismo idioma que handshake)
    if (this.socket) {
      const activeOrConnecting = this.socket.connected || this.socket.active || this.isConnecting || !!this.connectionPromise;
      if (activeOrConnecting) {
        logEvent('WS_ALREADY_EXISTS', 'socketService', { userId, reason: 'reuse_instance' });
        if (this.socket.io?.opts) {
          this.socket.io.opts.query = { ...queryPayload };
        }
        this.lastConnectLanguage = language;
        if (typeof window !== 'undefined') {
          window.__ARCHAT_SOCKET__ = this.socket;
        }
        devLog('⚠️ [SOCKET] Reutilizando instancia existente (conectado o conectando)');
        return this.socket;
      }

      // Reusar la misma instancia cuando quedó desconectada
      this.isConnecting = true;
      this.socketReady = false;
      this.isConnected = false;
      if (this.socket.io?.opts) {
        this.socket.io.opts.query = { ...queryPayload };
      }
      this.lastConnectLanguage = language;
      if (typeof window !== 'undefined') {
        window.__ARCHAT_SOCKET__ = this.socket;
      }
      if (!this.connectionPromise) {
        this.connectionPromise = new Promise((resolve) => {
          this.connectionResolve = resolve;
        });
      }
      this.socket.connect();
      return this.socket;
    }

    // Crear promesa de conexión para instancia nueva
    if (!this.connectionPromise) {
      this.connectionPromise = new Promise((resolve) => {
        this.connectionResolve = resolve;
      });
    }
    this.isConnecting = true;

    const { token, userId: qUserId, tenantId, language: qLanguage } = queryPayload;
    const socketOptions = {
      path: '/socket.io/',
      transports: ['polling', 'websocket'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      timeout: 10000,
      query: {
        token,
        userId: qUserId,
        tenantId,
        language: qLanguage,
      },
    };

    devLog('🔌 [SOCKET] path:', socketOptions.path);

    // Por si quedó una instancia huérfana en window (otra pestaña o HMR): cerrar antes de io().
    if (typeof window !== 'undefined' && window.__ARCHAT_SOCKET__) {
      try {
        console.warn('[SOCKET_KILL_PREVIOUS]');
        window.__ARCHAT_SOCKET__.disconnect();
      } catch (_) {
        // noop
      }
      window.__ARCHAT_SOCKET__ = null;
    }

    wsLog('SOCKET_INIT', {
      url: SOCKET_URL,
      query: {
        ...socketOptions.query,
        token: socketOptions.query?.token ? '[redacted]' : socketOptions.query?.token,
      },
    });

    if (this.socket) {
      const oldSock = this.socket;
      try {
        oldSock.removeAllListeners?.();
      } catch (_) {
        // noop
      }
      try {
        oldSock.disconnect();
      } catch (_) {
        // noop
      }
      this.socket = null;
      if (typeof window !== 'undefined') {
        if (window.__ARCHAT_SOCKET__ === oldSock) window.__ARCHAT_SOCKET__ = null;
        if (window.__ARCHAT_ACTIVE_SOCKET__ === oldSock) window.__ARCHAT_ACTIVE_SOCKET__ = null;
      }
    }

    if (this.socket) {
      logEvent('WS_ALREADY_EXISTS', 'socketService', { userId, reason: 'pre_io_instance' });
      return this.socket;
    }

    if (typeof console !== 'undefined') {
      console.log('[SOCKET_IO_TYPE]', typeof io);
    }
    if (typeof io !== 'function') {
      console.error('[SOCKET_IO_INVALID]', 'socket.io-client no exporta io() como función; revisar dependencias y bundler.');
      this.isConnecting = false;
      return this.socket;
    }

    if (typeof console !== 'undefined') {
      console.log('[SOCKET_CONNECT_CALLED]');
    }

    if (typeof console !== 'undefined') {
      console.log('[SOCKET_URL]', SOCKET_URL);
    }

    if (typeof window !== 'undefined' && window.__ARCHAT_SOCKET__) {
      console.warn('[SOCKET_PREVIOUS_FOUND]');
    }

    logEvent('WS_CONNECT', 'socketService', { userId: qUserId });
    this.socket = io(SOCKET_URL, socketOptions);
    this._flushPendingListeners();
    this.lastConnectLanguage = language;

    if (typeof window !== 'undefined') {
      window.__ARCHAT_SOCKET__ = this.socket;
      window.__ARCHAT_ACTIVE_SOCKET__ = this.socket;
    }

    this.socket.on('connect', () => {
      logEvent('WS_CONNECTED', 'socketService', { socketId: this.socket?.id ?? null });
      if (typeof console !== 'undefined') {
        console.log('[SOCKET_CONNECTED_OK]', this.socket?.id ?? null);
        if (this._chatMessageHandler) {
          console.error('[HANDLER_REATTACHED]');
        }
        if (typeof window !== 'undefined') {
          window.__ARCHAT_HANDLER__ = this._chatMessageHandler;
          console.error('[HANDLER_GLOBAL]', window.__ARCHAT_HANDLER__);
        }
      }
      wsLog('SOCKET_CONNECTED', this.socket?.id ?? null);
    });
    this.socket.on('connect_error', (err) => {
      const msg = err?.message ?? String(err);
      logEvent('WS_CONNECT_ERROR', 'socketService', { message: msg });
      console.error('[SOCKET_CONNECT_ERROR]', msg, err);
      wsLog('SOCKET_CONNECT_ERROR', msg);
      console.error('[WS_CONNECT_ERROR]', msg);
    });
    this.socket.on('disconnect', (reason) => {
      console.warn('[SOCKET_DISCONNECTED]', reason);
      wsLog('SOCKET_DISCONNECT', reason);
    });
    if (typeof this.socket.onAny === 'function') {
      this.socket.onAny((event, ...args) => {
        wsLog('WS_ANY_EVENT', { event, args });
        if (typeof console !== 'undefined') {
          console.log('[WS_EVENT]', event, args);
        }
      });
    }
    this._bindMessageHandler();

    this.socket.on('chat:message:test', (data) => {
      try {
        console.log('[WS_TEST_RECEIVED]', data);
      } catch (_) {
        // noop
      }
    });
    this.socket.on('ws:joined', (data) => {
      try {
        const uid = this.userId ?? data?.userId ?? data?.user_id ?? null;
        logEvent('WS_JOIN', 'socketService.ws:joined', { userId: uid, raw: data ?? {} });
        console.log('[WS_JOIN_CONFIRMED]', data);
      } catch (_) {
        // noop
      }
    });
    this.socket.on('ws:test', (data) => {
      try {
        console.log('[WS_TEST_OK]', data);
      } catch (_) {
        // noop
      }
    });

    try {
      console.log('[SOCKET_CREATED_WITH_LANG]', { language });
    } catch (_) {
      // noop
    }
    try {
      console.warn('[SOCKET_CREATED_BY_SERVICE]', this.socket.io.opts);
    } catch (_) {
      // noop
    }

    this.socket.on('inapp_notification', (payload) => {
      try {
        if (payload && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('archat:inapp_notification', { detail: payload }));
        }
      } catch (e) {
        devLog('[SOCKET][inapp_notification]', e?.message || e);
      }
    });

    devLog('[SOCKET][BOOT] chat:message → setChatHandler (store directo)');

    // Eventos de conexión
    this.socket.on('connect', () => {
      try {
        console.log('[SOCKET_CONNECT]', this.socket?.id);
        console.log('[WS_CONNECTED_REAL]', {
          id: this.socket?.id,
          url: this._lastSocketUrl ?? (typeof window !== 'undefined' ? window.location.origin : ''),
        });
      } catch (_) {
        // noop
      }
      if (this.userId) {
        const freshQuery = this.buildQueryPayload(this.userId);
        if (freshQuery && this.socket.io?.opts) {
          this.socket.io.opts.query = { ...freshQuery };
        }
      }
      this.isConnected = true;
      this.isConnecting = false;
      this.socketReady = true;
      this.reconnectAttempts = 0;
      if (typeof window !== 'undefined') {
        window.__ARCHAT_SOCKET__ = this.socket;
        if (window.__ARCHAT_E2E_EXPOSE_SOCKET__) {
          window.__ARCHAT_E2E_SOCKET__ = this.socket;
        }
        window.__ARCHAT_HANDLER__ = this._chatMessageHandler;
        if (typeof console !== 'undefined') {
          if (this._chatMessageHandler) {
            console.error('[HANDLER_REATTACHED]');
          }
          console.error('[HANDLER_GLOBAL]', window.__ARCHAT_HANDLER__);
        }
      }
      devLog('✅ [SOCKET] Socket conectado correctamente');
      devLog('🔌 [SOCKET] socket.id =', this.socket.id);
      devLog('🔌 [SOCKET] Namespace =', this.socket.nsp.name);
      devLog('🔌 [SOCKET] Transport =', this.socket.io.engine.transport.name);
      
      // Resolver promesa de conexión
      if (this.connectionResolve) {
        this.connectionResolve(true);
        this.connectionResolve = null;
        this.connectionPromise = null;
      }
      
      // CORRECCIÓN: Procesar mensajes pendientes
      this.processPendingMessages();
      this.processPendingEvents();

      // Sincronizar idioma actual con la DB al conectar (solo si difiere del último persistido)
      const currentLang = typeof localStorage !== 'undefined' ? localStorage.getItem('archat_language') : null;
      const lastPersisted = typeof localStorage !== 'undefined' ? localStorage.getItem('archat_lang_persisted') : null;
      if (currentLang && currentLang !== lastPersisted) {
        const normalizedConnectLang = normalizeLang(currentLang);
        if (normalizedConnectLang) {
          this._persistLanguagePreference(normalizedConnectLang);
        }
      }
    });

    // CORRECCIÓN: Listener para socket_ready del backend
    this.socket.on('socket_ready', (data) => {
      devLog('✅ [SOCKET] Socket listo confirmado por backend:', data);
      this.socketReady = true;
      this.isConnected = true;
      
      // Procesar mensajes pendientes cuando el socket confirma que está listo
      this.processPendingMessages();
      this.processPendingEvents();
    });

    this.socket.on('disconnect', (reason) => {
      logEvent('WS_DISCONNECTED', 'socketService', { reason: reason ?? null });
      this.isConnected = false;
      this.isConnecting = false;
      this.socketReady = false;
      if (typeof window !== 'undefined' && window.__ARCHAT_SOCKET__ === this.socket) {
        window.__ARCHAT_SOCKET__ = null;
      }
      devLog('❌ [SOCKET] Desconectado:', reason);
    });

    this.socket.on('connect_error', (error) => {
      this.reconnectAttempts++;
      this.isConnected = false;
      this.socketReady = false;
      this.isConnecting = true;
      console.error('❌ [SOCKET] Error de conexión:', error.message);
      console.error('❌ [SOCKET] Tipo de error:', error.type);
      console.error('❌ [SOCKET] Descripción:', error.description);
      console.error('❌ [SOCKET] Contexto:', {
        url: this._lastSocketUrl ?? (typeof window !== 'undefined' ? window.location.origin : ''),
        path: '/socket.io/',
        userId: userId,
        intento: this.reconnectAttempts
      });
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('❌ [SOCKET] Máximo de intentos de reconexión alcanzado');
        this.isConnecting = false;
        
        // Resolver promesa con false si se agotaron los intentos
        if (this.connectionResolve) {
          this.connectionResolve(false);
          this.connectionResolve = null;
          this.connectionPromise = null;
        }
      }
    });

    this.socket.on('reconnect', (attemptNumber) => {
      if (this.userId) {
        const freshQuery = this.buildQueryPayload(this.userId);
        if (freshQuery && this.socket.io?.opts) {
          this.socket.io.opts.query = { ...freshQuery };
        }
      }
      devLog('🔄 [SOCKET] Reconectado después de', attemptNumber, 'intentos');
      this.isConnected = true;
      this.isConnecting = false;
      this.socketReady = true;
      if (typeof window !== 'undefined') {
        window.__ARCHAT_SOCKET__ = this.socket;
        if (window.__ARCHAT_E2E_EXPOSE_SOCKET__) {
          window.__ARCHAT_E2E_SOCKET__ = this.socket;
        }
        window.__ARCHAT_HANDLER__ = this._chatMessageHandler;
        if (typeof console !== 'undefined') {
          if (this._chatMessageHandler) {
            console.error('[HANDLER_REATTACHED]');
          }
          console.error('[HANDLER_GLOBAL]', window.__ARCHAT_HANDLER__);
        }
      }
      // Procesar cola después de reconexión
      this.processPendingMessages();
      this.processPendingEvents();
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      if (this.userId) {
        const freshQuery = this.buildQueryPayload(this.userId);
        if (freshQuery && this.socket.io?.opts) {
          this.socket.io.opts.query = { ...freshQuery };
        }
      }
      devLog('🔄 [SOCKET] Intento de reconexión', attemptNumber);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('❌ [SOCKET] Falló la reconexión');
      this.isConnected = false;
      this.isConnecting = false;
      this.socketReady = false;
      
      // Resolver promesa con false
      if (this.connectionResolve) {
        this.connectionResolve(false);
        this.connectionResolve = null;
        this.connectionPromise = null;
      }
    });

    // ACK de entrega (confirmación al emisor)
    this.socket.on('chat:ack', (data) => {
      if (window.DEBUG_SOCKET) {
        devLog('[WS][RECV] chat:ack', data);
      }
      window.dispatchEvent(new CustomEvent('archat:chat_ack', { detail: data }));
    });

    // CORRECCIÓN: Listener para nuevas conversaciones
    this.socket.on('new_conversation', (data) => {
      try {
        devLog('🆕 [SOCKET] Evento new_conversation recibido:', data);
        window.dispatchEvent(new CustomEvent('archat:new_conversation', { detail: data }));
      } catch (error) {
        console.error('⚠️ [SOCKET] Error procesando new_conversation:', error);
      }
    });

    // Retry de entrega: servidor reenvía mensaje no entregado; frontend puede pedirlo por API si no está en memoria
    this.socket.on('chat:retry_message', (data) => {
      try {
        window.dispatchEvent(new CustomEvent('archat:retry_message', { detail: data || {} }));
      } catch (error) {
        console.error('⚠️ [SOCKET] Error procesando chat:retry_message:', error);
      }
    });

    this.socket.on('chat:sync_message', (data) => {
      try {
        const d = data || {};
        if (typeof this._chatMessageHandler !== 'function') return;
        const cid = d.conversation_id ?? d.conversacion_id;
        const mid = d.message_id ?? d.id;
        if (cid == null || mid == null) return;
        const body = d.message ?? d.text ?? d.mensaje ?? d.display_text ?? '';
        const senderResolved =
          d.sender_id ??
          d.remitente_id ??
          d.emisor_id ??
          d.usuario_id ??
          d.from_user_id ??
          null;
        const normalized = {
          id: mid,
          conversation_id: cid,
          conversacion_id: cid,
          sender_id: senderResolved,
          remitente_id: d.remitente_id ?? senderResolved,
          text: body,
          display_text: body,
          mensaje: body,
          fecha_envio: d.created_at ?? d.fecha_envio ?? d.timestamp,
          delivery_status: d.delivery_status,
        };
        this._chatMessageHandler(normalized);
        if (isRecentIncomingForSound(d)) {
          window.dispatchEvent(new CustomEvent('archat:new_message', { detail: normalized }));
          publishIncomingChatMessage(normalized);
        }
      } catch (error) {
        console.error('⚠️ [SOCKET] Error procesando chat:sync_message:', error);
      }
    });

    this.socket.on('chat:sync_batch', (messages) => {
      try {
        if (typeof this._chatMessageHandler !== 'function') return;
        const batch = Array.isArray(messages) ? messages : [];
        batch.forEach((d) => {
          if (!d) return;
          const mid = d.message_id ?? d.id;
          const cid = d.conversation_id ?? d.conversacion_id;
          if (cid == null || mid == null) return;
          const body = d.message ?? d.text ?? d.mensaje ?? d.display_text ?? '';
          const senderResolved =
            d.sender_id ??
            d.remitente_id ??
            d.emisor_id ??
            d.usuario_id ??
            d.from_user_id ??
            null;
          const normalized = {
            id: mid,
            conversation_id: cid,
            conversacion_id: cid,
            sender_id: senderResolved,
            remitente_id: d.remitente_id ?? senderResolved,
            text: body,
            display_text: body,
            mensaje: body,
            fecha_envio: d.created_at ?? d.fecha_envio ?? d.timestamp,
            delivery_status: d.delivery_status,
          };
          this._chatMessageHandler(normalized);
          if (isRecentIncomingForSound(d)) {
            window.dispatchEvent(new CustomEvent('archat:new_message', { detail: normalized }));
            publishIncomingChatMessage(normalized);
          }
        });
      } catch (error) {
        console.error('⚠️ [SOCKET] Error procesando chat:sync_batch:', error);
      }
    });

    this.socket.on('chat:message_deleted', (payload) => {
      try {
        const message_id = payload?.message_id;
        const conversation_id = payload?.conversation_id ?? payload?.conversacion_id;
        const conversacion_id = payload?.conversacion_id ?? payload?.conversation_id;
        devLog('[DELETE_EVENT_RECEIVED]', {
          message_id,
          conversation_id: conversation_id ?? conversacion_id ?? null
        });
        devLog('[MSG_DELETED_RECEIVE]', message_id);
        window.dispatchEvent(new CustomEvent('archat:message_deleted', {
          detail: {
            message_id,
            conversation_id: conversation_id ?? conversacion_id ?? null,
            conversacion_id: conversacion_id ?? conversation_id ?? null,
            deleted_at: payload?.deleted_at ?? null,
            deleted_by: payload?.deleted_by ?? null,
          }
        }));
      } catch (error) {
        console.error('⚠️ [SOCKET] Error procesando chat:message_deleted:', error);
      }
    });

    this.socket.on('chat:message_edited', (payload) => {
      try {
        const message_id = payload?.message_id ?? payload?.mensaje_id;
        const conversation_id = payload?.conversation_id ?? payload?.conversacion_id;
        const conversacion_id = payload?.conversacion_id ?? payload?.conversation_id;
        window.dispatchEvent(
          new CustomEvent('archat:message_edited', {
            detail: {
              message_id,
              mensaje_id: message_id,
              conversation_id: conversation_id ?? conversacion_id ?? null,
              conversacion_id: conversacion_id ?? conversation_id ?? null,
              mensaje: payload?.mensaje ?? payload?.text ?? payload?.contenido ?? '',
              text: payload?.text ?? payload?.mensaje ?? payload?.contenido ?? '',
              contenido_original: payload?.contenido_original ?? payload?.original_text ?? '',
              original_text: payload?.original_text ?? payload?.contenido_original ?? '',
              source_language: payload?.source_language ?? payload?.idioma_original ?? null,
              translations_map: payload?.translations_map ?? payload?.traducciones ?? {},
              traducciones: payload?.traducciones ?? payload?.translations_map ?? {},
              edited_by: payload?.edited_by ?? payload?.sender_id ?? null,
              editado: payload?.editado ?? 1,
              fecha_edicion: payload?.fecha_edicion ?? payload?.updated_at ?? null,
              updated_at: payload?.updated_at ?? payload?.fecha_edicion ?? null,
            },
          })
        );
      } catch (error) {
        console.error('⚠️ [SOCKET] Error procesando chat:message_edited:', error);
      }
    });

    this.socket.on('presence:update', (data) => {
      try {
        window.dispatchEvent(
          new CustomEvent('archat:presence_update', { detail: data || {} })
        );
      } catch (error) {
        console.error('⚠️ [SOCKET] Error procesando presence:update:', error);
      }
    });

    this.socket.on('typing:start', (data) => {
      try {
        window.dispatchEvent(
          new CustomEvent('archat:typing_start', { detail: data || {} })
        );
      } catch (error) {
        console.error('⚠️ [SOCKET] Error procesando typing:start:', error);
      }
    });

    this.socket.on('typing:stop', (data) => {
      try {
        window.dispatchEvent(
          new CustomEvent('archat:typing_stop', { detail: data || {} })
        );
      } catch (error) {
        console.error('⚠️ [SOCKET] Error procesando typing:stop:', error);
      }
    });

    this.socket.on('message:delivered', (data) => {
      try {
        const recipientId = Number(data?.recipient_id);
        if (Number.isFinite(recipientId) && Number(this.userId) === recipientId) {
          // Este evento está destinado al remitente; si coincide con receptor, ignorar para evitar ruido.
          return;
        }
        window.dispatchEvent(
          new CustomEvent('archat:message_delivered', { detail: data || {} })
        );
      } catch (error) {
        console.error('⚠️ [SOCKET] Error procesando message:delivered:', error);
      }
    });
    this.socket.on('message:read', (data) => {
      try {
        window.dispatchEvent(
          new CustomEvent('archat:message_read', { detail: data || {} })
        );
      } catch (error) {
        console.error('⚠️ [SOCKET] Error procesando message:read:', error);
      }
    });

    this.socket.on('system:notice', (data) => {
      try {
        window.dispatchEvent(new CustomEvent('archat:system_notice', { detail: data || {} }));
      } catch (error) {
        console.error('⚠️ [SOCKET] Error system:notice:', error);
      }
    });
    this.socket.on('system:alert', (data) => {
      try {
        window.dispatchEvent(new CustomEvent('archat:system_alert', { detail: data || {} }));
      } catch (error) {
        console.error('⚠️ [SOCKET] Error system:alert:', error);
      }
    });
    this.socket.on('system:warning', (data) => {
      try {
        window.dispatchEvent(new CustomEvent('archat:system_warning', { detail: data || {} }));
      } catch (error) {
        console.error('⚠️ [SOCKET] Error system:warning:', error);
      }
    });

    this.socket.on('profile_updated', (data) => {
      try {
        invalidateCacheByPattern(['contactos']);
        window.dispatchEvent(new CustomEvent('archat:contact-updated', { detail: data || {} }));
      } catch (e) {
        console.warn('[LANG_WS_RECEIVE_ERR]', e?.message || e);
      }
    });

    devLog('✅ [SOCKET] Listeners globales registrados');
    return this.socket;
  }

  /**
   * Obtiene la instancia actual del socket singleton
   * @returns {import('socket.io-client').Socket|null}
   */
  getSocket() {
    return this.socket;
  }

  /**
   * CORRECCIÓN: Procesa mensajes pendientes cuando el socket se conecta
   */
  processPendingMessages() {
    if (!this.socketReady || this.pendingMessages.length === 0) {
      return;
    }

    devLog(`🚀 [SOCKET] Socket listo → enviando ${this.pendingMessages.length} mensajes pendientes`);
    
    const messagesToSend = [...this.pendingMessages];
    this.pendingMessages = [];
    
    messagesToSend.forEach((messageData) => {
      devLog(`📤 [SOCKET] Enviando mensaje pendiente:`, messageData);
      devLog('[TRACE] SEND WS CLIENT', messageData.data);
      try {
        logEvent('WS_EMIT', 'socketService.processPendingMessages', {
          event: 'send_message',
          data: messageData.data
        });
      } catch (_) {
        // noop
      }
      this.socket.emit('send_message', messageData.data);
      if (messageData.callback) {
        messageData.callback({ success: true, sent_via: 'socket_queue' });
      }
    });
  }

  /**
   * CORRECCIÓN: Procesa eventos pendientes cuando el socket se conecta
   */
  processPendingEvents() {
    if (!this.socketReady || this.pendingEvents.length === 0) {
      return;
    }

    devLog(`🚀 [SOCKET] Socket listo → enviando ${this.pendingEvents.length} eventos pendientes`);
    
    const eventsToSend = [...this.pendingEvents];
    this.pendingEvents = [];
    
    eventsToSend.forEach((eventData) => {
      devLog(`📤 [SOCKET] Enviando evento pendiente:`, eventData.event, eventData.data);
      this.socket.emit(eventData.event, eventData.data);
    });
  }

  /**
   * Desconecta del servidor
   */
  disconnect() {
    if (this.socket) {
      if (typeof window !== 'undefined' && window.__ARCHAT_SOCKET__ === this.socket) {
        window.__ARCHAT_SOCKET__ = null;
      }
      this.socket.disconnect();
      this.socket = null;
    }
    this._pendingListeners = [];
    this.isConnected = false;
    this.isConnecting = false;
    this.socketReady = false;
    this.userId = null;
    this.lastConnectLanguage = null;
    
    // Limpiar promesas pendientes
    if (this.connectionResolve) {
      this.connectionResolve(false);
      this.connectionResolve = null;
      this.connectionPromise = null;
    }
    
    devLog('🔌 [SOCKET] Desconectado');
  }

  /**
   * CORRECCIÓN: Emite un evento al servidor con sistema de cola
   * @param {string} event - Nombre del evento
   * @param {Object} data - Datos a enviar
   * @param {Object} options - Opciones adicionales
   */
  emit(event, data, options = {}) {
    const { 
      queueIfNotConnected = true, 
      callback = null,
      priority = 'normal' 
    } = options;

    if (!this.socket) {
      console.warn('⚠️ [SOCKET] Socket no inicializado, no se puede emitir:', event);
      if (callback) callback({ success: false, error: 'Socket no inicializado' });
      return;
    }

    if (!this.socketReady) {
      if (queueIfNotConnected && event === 'send_message') {
        // CORRECCIÓN: Encolar mensaje si no está conectado
        if (this.pendingMessages.length >= this.maxPendingMessages) {
          console.warn('⚠️ [SOCKET] Cola de mensajes llena, descartando mensaje más antiguo');
          this.pendingMessages.shift();
        }
        
        this.pendingMessages.push({
          event: event,
          data: data,
          timestamp: Date.now(),
          callback: callback
        });
        
        devLog(`📋 [SOCKET] Mensaje encolado esperando socket (${this.pendingMessages.length} pendientes)`);
        return;
      } else if (queueIfNotConnected) {
        // Encolar otros eventos si es necesario
        this.pendingEvents.push({
          event: event,
          data: data,
          timestamp: Date.now()
        });
        
        devLog(`📋 [SOCKET] Evento encolado esperando socket:`, event);
        return;
      } else {
        console.warn('⚠️ [SOCKET] No conectado, no se puede emitir:', event);
        if (callback) callback({ success: false, error: 'Socket no conectado' });
        return;
      }
    }

    devLog('📤 [SOCKET] Emit:', event, data);
    if (event === 'send_message') {
      try {
        logEvent('WS_EMIT', 'socketService.emit', { event, data });
      } catch (_) {
        // noop
      }
      devLog('[TRACE] SEND WS CLIENT', data);
    }
    this.socket.emit(event, data);
    
    if (callback) {
      callback({ success: true, sent_via: 'socket_direct' });
    }
  }

  /**
   * Escucha un evento del servidor
   * @param {string} event - Nombre del evento
   * @param {Function} callback - Función callback
   */
  on(event, callback) {
    if (!this.socket) {
      this._pendingListeners.push({ event, callback });
      devLog('📋 [SOCKET] Listener encolado (socket pendiente):', event);
      return;
    }

    devLog('👂 [SOCKET] Escuchando:', event);
    this.socket.on(event, callback);
  }

  /**
   * Deja de escuchar un evento
   * @param {string} event - Nombre del evento
   * @param {Function} callback - Función callback (opcional)
   */
  off(event, callback) {
    if (!this.socket) {
      if (callback) {
        this._pendingListeners = this._pendingListeners.filter(
          (x) => !(x.event === event && x.callback === callback)
        );
      } else {
        this._pendingListeners = this._pendingListeners.filter((x) => x.event !== event);
      }
      return;
    }

    devLog('🔇 [SOCKET] Dejando de escuchar:', event);
    if (callback) {
      this.socket.off(event, callback);
    } else {
      this.socket.removeAllListeners(event);
    }
  }

  /**
   * CORRECCIÓN: Envía un mensaje con envío resiliente
   * @param {number|null} conversacionId - ID de la conversación (puede ser null para creación automática)
   * @param {number|null} contactoId - ID del contacto (para creación automática)
   * @param {string} mensaje - Texto del mensaje
   * @param {string} idiomaOriginal - Idioma original (opcional)
   * @param {Function} callback - Callback para confirmar envío
   */
  sendMessageResilient(conversacionId, contactoId, mensaje, idiomaOriginal = null, callback = null) {
    const messageData = {
      mensaje: mensaje,
      idioma_original: idiomaOriginal
    };

    // Agregar conversacion_id o contacto_id según disponibilidad
    if (conversacionId) {
      messageData.conversacion_id = conversacionId;
    }
    if (contactoId) {
      messageData.contacto_id = contactoId;
    }

    devLog('📤 [SOCKET] Enviando mensaje resiliente:', { conversacionId, contactoId, socketReady: this.socketReady });

    this.emit('send_message', messageData, {
      queueIfNotConnected: true,
      callback: callback,
      priority: 'high'
    });
  }

  /**
   * Emite un evento y espera una respuesta (acknowledgment)
   * @param {string} event - Nombre del evento
   * @param {Object} data - Datos a enviar
   * @returns {Promise} Promesa que se resuelve con la respuesta
   */
  emitWithAck(event, data) {
    return new Promise(async (resolve, reject) => {
      // CORRECCIÓN: Esperar a que el socket esté listo
      const isReady = await this.waitForReady(5000);
      if (!isReady) {
        reject(new Error('Socket no conectado después del timeout'));
        return;
      }

      this.socket.emit(event, data, (response) => {
        if (response && response.error) {
          reject(new Error(response.error));
        } else {
          resolve(response);
        }
      });

      // Timeout de 10 segundos
      setTimeout(() => {
        reject(new Error('Timeout esperando respuesta'));
      }, 10000);
    });
  }

  /**
   * LEGACY: Mantener compatibilidad con métodos anteriores
   */
  sendMessage(conversacionId, mensaje, idiomaOriginal = null) {
    this.sendMessageResilient(conversacionId, null, mensaje, idiomaOriginal);
  }

  /**
   * Obtiene mensajes de una conversación
   * @param {number} conversacionId - ID de la conversación
   * @param {number} limit - Límite de mensajes
   * @param {number} offset - Offset para paginación
   */
  getMessages(conversacionId, limit = 50, offset = 0) {
    this.emit('get_messages', {
      conversacion_id: conversacionId,
      limit,
      offset
    });
  }

  /**
   * Se une a una conversación
   * @param {number} conversacionId - ID de la conversación
   * @param {number} userId - ID del usuario (opcional)
   */
  joinConversation(conversacionId, userId = null) {
    this.emit('join_conversation', {
      conversacion_id: conversacionId,
      usuario_id: userId
    });
  }

  /**
   * Sale de una conversación
   * @param {number} conversacionId - ID de la conversación
   * @param {number} userId - ID del usuario (opcional)
   */
  leaveConversation(conversacionId, userId = null) {
    this.emit('leave_conversation', {
      conversacion_id: conversacionId,
      usuario_id: userId
    });
  }

  /**
   * Emite typing:start para indicador "X está escribiendo..." (evita spam con debounce en el input).
   * @param {number|string} conversationId - ID de la conversación
   * @param {number|string} userId - ID del usuario actual
   * @param {string} name - Nombre a mostrar
   */
  sendTypingStart(conversationId, userId, name) {
    if (!this.socket?.connected || conversationId == null || userId == null) return;
    this.socket.emit('typing:start', {
      conversation_id: conversationId,
      user_id: userId,
      name: name || ''
    });
  }

  /**
   * Emite typing:stop al dejar de escribir (debounce ~1500 ms en el input).
   * @param {number|string} conversationId - ID de la conversación
   * @param {number|string} userId - ID del usuario actual
   */
  sendTypingStop(conversationId, userId) {
    if (!this.socket?.connected || conversationId == null || userId == null) return;
    this.socket.emit('typing:stop', {
      conversation_id: conversationId,
      user_id: userId
    });
  }

  /**
   * Indica que el usuario está escribiendo
   * @param {number} conversacionId - ID de la conversación
   * @param {boolean} isTyping - Si está escribiendo o no
   */
  setTyping(conversacionId, isTyping) {
    this.emit('typing', {
      conversacion_id: conversacionId,
      is_typing: isTyping
    }, { queueIfNotConnected: false }); // No encolar eventos de typing
  }

  /**
   * Obtiene estadísticas de la cola
   * @returns {Object} Estadísticas de mensajes pendientes
   */
  getQueueStats() {
    return {
      pendingMessages: this.pendingMessages.length,
      pendingEvents: this.pendingEvents.length,
      socketReady: this.socketReady,
      isConnected: this.isConnected
    };
  }
}

// Instancia singleton
const socketService = new SocketService();

export default socketService;
